import React, { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Search, Edit, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export const Owners = () => {
  const [owners, setOwners] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingOwnerId, setDeletingOwnerId] = useState(null);
  const [editingOwner, setEditingOwner] = useState(null);
  const [formData, setFormData] = useState({ full_name: '', address: '', phone: '', email: '' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchOwners();
  }, []);

  const fetchOwners = async () => {
    try {
      const response = await api.get('/owners');
      setOwners(response.data);
    } catch (error) {
      toast.error('Failed to load owners');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOwner) {
        await api.put(`/owners/${editingOwner.id}`, formData);
        toast.success('Owner updated successfully');
      } else {
        await api.post('/owners', formData);
        toast.success('Owner created successfully');
      }
      setIsDialogOpen(false);
      setFormData({ full_name: '', address: '', phone: '', email: '' });
      setEditingOwner(null);
      fetchOwners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (owner) => {
    setEditingOwner(owner);
    setFormData({
      full_name: owner.full_name,
      address: owner.address,
      phone: owner.phone,
      email: owner.email,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id) => {
    setDeletingOwnerId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/owners/${deletingOwnerId}`);
      toast.success('Owner deleted successfully');
      fetchOwners();
    } catch (error) {
      toast.error('Failed to delete owner');
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingOwnerId(null);
    }
  };

  const filteredOwners = owners.filter((owner) =>
    owner.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    owner.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 md:p-12" data-testid="owners-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-sage-800 mb-2">Owners</h1>
          <p className="text-gray-600">Manage pet owners and their information</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-sage-500 hover:bg-sage-600 text-white rounded-full shadow-lg"
              onClick={() => {
                setEditingOwner(null);
                setFormData({ full_name: '', address: '', phone: '', email: '' });
              }}
              data-testid="add-owner-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Owner
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle>{editingOwner ? 'Edit Owner' : 'Add New Owner'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="John Doe"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 234 567 8900"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main St, City"
                  className="mt-1"
                />
              </div>
              <Button type="submit" className="w-full bg-sage-500 hover:bg-sage-600 text-white rounded-xl">
                {editingOwner ? 'Update Owner' : 'Create Owner'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            placeholder="Search owners by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 rounded-xl"
            data-testid="owners-search-input"
          />
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
        {/* Mobile card view */}
        <div className="md:hidden">
          {filteredOwners.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              No owners found. Add your first owner to get started!
            </div>
          ) : (
            filteredOwners.map((owner, idx) => (
              <div key={owner.id} className="border-b border-gray-100 p-4" data-testid={`owner-row-${idx}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="font-semibold text-gray-800">{owner.full_name}</p>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/dashboard/owners/${owner.id}`)} className="text-sage-600 hover:bg-sage-50 p-1.5" data-testid={`view-owner-${idx}`}><Eye className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(owner)} className="p-1.5" data-testid={`edit-owner-${idx}`}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(owner.id)} className="text-red-600 hover:bg-red-50 p-1.5" data-testid={`delete-owner-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                <p className="text-sm text-gray-500">{owner.email}</p>
                {owner.phone && <p className="text-sm text-gray-500">{owner.phone}</p>}
                {owner.address && <p className="text-sm text-gray-500 truncate">{owner.address}</p>}
              </div>
            ))
          )}
        </div>
        {/* Desktop table view */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-sage-50 border-b border-sage-100">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-sage-800">Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-sage-800">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-sage-800">Phone</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-sage-800">Address</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-sage-800">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOwners.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    No owners found. Add your first owner to get started!
                  </td>
                </tr>
              ) : (
                filteredOwners.map((owner, idx) => (
                  <tr
                    key={owner.id}
                    className="border-b border-gray-100 hover:bg-sage-50 transition-colors"
                    data-testid={`owner-row-${idx}`}
                  >
                    <td className="px-6 py-4 font-medium text-gray-800">{owner.full_name}</td>
                    <td className="px-6 py-4 text-gray-600">{owner.email}</td>
                    <td className="px-6 py-4 text-gray-600">{owner.phone}</td>
                    <td className="px-6 py-4 text-gray-600">{owner.address}</td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/dashboard/owners/${owner.id}`)}
                        className="mr-1 text-sage-600 hover:text-sage-800 hover:bg-sage-50"
                        data-testid={`view-owner-${idx}`}
                        title="View owner"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(owner)}
                        className="mr-1"
                        data-testid={`edit-owner-${idx}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(owner.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-owner-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the owner and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
